import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { LuListFilter, LuRotateCcw } from 'react-icons/lu';

import { optimiserByBlastQueryOptions } from '~api/queries';
import type { PatternDesignOptimisation } from '~api/services';
import { FilterDropdown, TableList, TooltipContent, TooltipRoot, TooltipTrigger } from '~components';
import { Button } from '~components/ui/button';
import { ScrollArea } from '~components/ui/scroll-area';
import { ROUTE_IDS } from '~config';
import { BlastOptimisationsTableColumns, useFilteredOptimisations } from '~features/blast-optimisations';
import { trackMetric } from '~libs';
import { useTableStore } from '~store';

const BlastOptimisations = () => {
  const sortingState = useTableStore((state) => state.sorting);
  const setSortingState = useTableStore((state) => state.setSorting);

  const filtering = useTableStore((state) => state.filtering);
  const setFilterValue = useTableStore((state) => state.setFilterValue);
  const clearFilter = useTableStore((state) => state.clearFilter);
  const resetFiltering = useTableStore((state) => state.resetFiltering);

  const navigate = useNavigate();
  const { blastName } = useParams({ strict: false });
  const trackedBlastNameRef = useRef<string | null>(null);

  // Fetch blast optimisations data - will use cached data if available from route prefetch
  const { data: blastOptimiserData, isLoading } = useQuery({
    ...optimiserByBlastQueryOptions(blastName ?? ''),
    enabled: !!blastName,
  });

  useEffect(() => {
    if (!blastName || !blastOptimiserData) return;
    if (trackedBlastNameRef.current === blastName) return;

    trackedBlastNameRef.current = blastName;
    trackMetric({
      name: 'optimisations_loaded_count',
      value: blastOptimiserData.optimisations.length,
      properties: {
        blastName,
      },
    });
  }, [blastName, blastOptimiserData]);

  const { filteredOptimisations, filterConfigs } = useFilteredOptimisations(blastOptimiserData?.optimisations ?? []);

  const selectedFilterValue = (columnId: string) => {
    const filter = filtering.find((f) => f.columnId === columnId);
    return filter?.value;
  };

  const onRowClick = (row: PatternDesignOptimisation) => {
    // Navigate to the pattern design details page
    navigate({
      to: ROUTE_IDS.BLAST_OPTIMISATION_VIEWER,
      params: { blastName: blastName as string, optimiserId: String(row.optimisation_id) },
    });
  };

  return (
    <div className="flex w-full flex-col">
      <div className="flex items-center gap-4 border-border border-b bg-background p-6 pb-4">
        <div className="flex gap-2 text-sm">
          <LuListFilter size={14} />
          Filters
        </div>
        <div className="h-6 border-border border-l" />
        {/* Filter Dropdowns */}
        <div className="flex min-h-11 flex-wrap items-center gap-4">
          {filterConfigs.map((config) => (
            <FilterDropdown
              key={config.columnId}
              label={config.displayLabel}
              value={selectedFilterValue(config.columnId) ?? null}
              options={(config.options ?? []).map((option) => ({ value: option.label, label: option.label }))}
              onSelect={(value) =>
                value === null ? clearFilter(config.columnId) : setFilterValue(config.columnId, value)
              }
            />
          ))}
          {/* Reset Filters */}
          {filtering.length > 0 && (
            <TooltipRoot>
              <TooltipTrigger asChild>
                <Button icon size="sm" variant="ghost" aria-label="Reset Filters" onClick={resetFiltering}>
                  <LuRotateCcw size={20} aria-hidden="true" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Reset Filters</TooltipContent>
            </TooltipRoot>
          )}
        </div>
      </div>
      <ScrollArea
        className="min-h-0 px-4 pt-6 sm:px-6"
        viewportClassName="flex grow pb-6"
        type="auto"
        orientation="both"
      >
        <TableList
          data={filteredOptimisations}
          columns={BlastOptimisationsTableColumns}
          sortingState={sortingState}
          isLoading={isLoading}
          onRowClick={onRowClick}
          onSortingChange={setSortingState}
          rowClassName={(row) => (!row.constrained ? 'bg-destructive-container' : undefined)}
        />
      </ScrollArea>
    </div>
  );
};

export { BlastOptimisations };
